import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { getFormattedCart } from "@/utils/functions";
import { useSelector } from "react-redux";
import { ReducersTypes } from "@/redux/reducers/Models";
import { FormattedDebts } from "@/utils/transform";
import { PropsView } from "@/pages/home/Models";
import useI18n from "@/hooks/useI18n";

export default function OrgPayment({
  disabled,
  textLabel,
  onClick,
  buttonType = "button",
}: {
  disabled?: boolean;
  textLabel?: string;
  onClick?: () => void;
  buttonType?: "submit" | "button";
}) {
  const navigate = useNavigate();
  const { t } = useI18n();
  const user = useSelector((state: ReducersTypes & {
    userReducer: {
      debts: FormattedDebts[];
      cart: PropsView['selectables'];
      plate?: string;
    }
  }) => state.userReducer);
  
  const customer = useSelector((state: ReducersTypes & {
    customerReducer: {
      client?: { id: string };
      selectedVehicles?: string[];
      vehicles?: { plate: string }[];
      debts?: FormattedDebts[];
      cart?: PropsView['selectables'];
    }
  }) => state.customerReducer);
  
  
  const isAuthenticated = customer?.client?.id;
  const currentCart = isAuthenticated ? customer.cart : user?.cart;
  const currentDebts = (isAuthenticated ? customer.debts : user?.debts) ?? [];
  
  let selectedPlate: string | string[] = "";
  
  if (isAuthenticated) {
    if (customer?.selectedVehicles?.length) {
      selectedPlate = customer.selectedVehicles;
    } else {
      selectedPlate = customer?.vehicles?.[0]?.plate ?? "";
    }
  } else {
    selectedPlate = user?.plate ?? "";
  }

  const [collapsed, setCollapsed] = useState(false);

  const toggleCollapsed = () => setCollapsed((collapsed) => !collapsed);

  const { total, filteredItems } = getFormattedCart({
    debts: currentDebts,
    cart: currentCart?.filter(item => item.checked) ?? [],
  });

  const isAvulsoProcess = !isAuthenticated;
  
  return (
    <div className="fixed bottom-0 left-1/2 -translate-x-1/2 z-10 bg-white w-[calc(480px)] max-w-[calc(96%)] shadow-md rounded-t-xl p-3">
      <div
        className={`mb-1 transition-max-height duration-500 ease-in-out ${
          collapsed ? "max-h-[243px] overflow-auto" : "max-h-0 overflow-hidden"
        }`}
      >
        {Array.isArray(selectedPlate) ? (
          selectedPlate.map((plate) => {
            // Filter items for this plate
            const plateItems = filteredItems.filter(debt => debt.plate === plate);

            // Only show plates that have items
            return plateItems && plateItems.length > 0 ? (
              <div key={plate} className="mb-3">
                <p className="text-lg mb-1 text-black">
                  {t("common:plate")}: <span className="uppercase">{plate.replace(/^([A-Za-z]{3})(\d[A-Za-z]?\d{2,3})$/, "$1-$2")}</span>
                </p>
                {plateItems.map((item, index) => (
                  <div key={index} className="flex w-full justify-between">
                    <p className="text-sm text-black">
                      {isAvulsoProcess ? item?.original?.nmConcessionaria : item?.original?.nmPraca}
                    </p>
                    <p className="text-sm text-black">R$ {item.price}</p>
                  </div>
                ))}
                <div className="w-full h-px bg-slate-300 mt-2 mb-2" />
              </div>
            ) : null;
          })
        ) : selectedPlate ? (
          <>
            <p className="text-lg mb-1 text-black">
              {t("common:plate")}: <span className="uppercase">{selectedPlate.replace(/^([A-Za-z]{3})(\d[A-Za-z]?\d{2,3})$/, "$1-$2")}</span>
            </p>
            {filteredItems?.map((item, index) => (
              <div key={index} className="flex w-full justify-between">
                <p className="text-sm text-black">
                  {isAvulsoProcess ? item?.original?.nmConcessionaria : item?.original?.nmPraca}
                </p>
                <p className="text-sm text-black">R$ {item.price}</p>
              </div>
            ))}
          </>
        ) : null}
      </div>
      <div className="w-full flex justify-between items-center">
        <p className="text-lg text-black">{t("pages:home:totalPayable")}:</p>
        <button
          type="button"
          onClick={toggleCollapsed}
          className={`duration-500 ease-in-out transform w-8 h-8 flex items-center justify-center ${total === 0 ? "opacity-50 cursor-not-allowed pointer-events-none" : "opacity-100 cursor-pointer"}`}
          style={{ transform: collapsed ? "rotate(180deg)" : "rotate(0deg)", transition: "opacity 0.3s ease" }}
          disabled={total === 0}
        >
          <img
            src="/svg/icon-arrow-up.svg"
            width={24}
            height={24}
            alt={collapsed ? t("common:collapseTotal") : t("common:expandTotal")}
          />
        </button>
      </div>
      <div className="w-full h-px bg-slate-300 mt-2 mb-2" />
      <div className="w-full flex justify-between items-center">
        <p className="text-xl flex items-end text-black">
          <span className="text-base">R$</span>
          <b>&nbsp;{total ? total?.toFixed(2)?.toString().replace(/\./g, ","): '0,00'}</b>
        </p>
        <button
          type={buttonType}
          disabled={disabled || total === 0}
          className={`${disabled || total === 0 ? "bg-gray-300 text-gray-600 opacity-50 cursor-not-allowed" : "bg-black text-white opacity-100 cursor-pointer"} p-2 rounded-md w-28 transition-opacity duration-300`}
          onClick={
            total > 0 && !disabled && buttonType === "button" ? (onClick || (() => navigate("/checkout"))) : () => null
          }
        >
          {textLabel ?? t("common:continue")}
        </button>
      </div>
    </div>
  );
}
